import { Button, Flex, Image, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import amatriumDark from 'assets/image/amatrium_dark.png';
import amatriumLight from 'assets/image/amatrium_light.png';

export interface AuthSplashProps {
  title: string;
  subtitle?: string;
  error?: string | null;
  retryCount?: number;
  onRetry?: () => void;
  retryLabel?: string;
  retryAttemptLabel?: string;
}

/**
 * Full-screen auth splash shown while Keycloak initializes / redirects to login, and during the
 * logout cleanup. Design-system styled (brand wordmark + DS tokens, theme-aware) — replaces the
 * old hardcoded dark-blue (#1a365d / #4299e1) inline screen so it matches the rest of the app.
 */
export const AuthSplash = ({
  title,
  subtitle,
  error,
  retryCount = 0,
  onRetry,
  retryLabel,
  retryAttemptLabel,
}: AuthSplashProps) => {
  const wordmark = useColorModeValue(amatriumDark, amatriumLight);
  return (
    <Flex
      pos='fixed'
      zIndex={9999}
      align='center'
      justify='center'
      direction='column'
      gap={8}
      px={6}
      bg='bg.canvas'
      inset={0}
    >
      <Image h='34px' objectFit='contain' alt='Amatrium' src={wordmark} />
      <Flex align='center' direction='column' gap={5} maxW='440px' textAlign='center'>
        <Text color={error ? 'state.error' : 'text.default'} fontSize='xl' fontWeight={600}>
          {title}
        </Text>
        {subtitle && !error && (
          <Text color='text.muted' fontSize='sm'>
            {subtitle}
          </Text>
        )}
        {error ? (
          <>
            <Text color='text.muted' fontSize='sm'>
              {error}
            </Text>
            {onRetry && (
              <Button
                px={6}
                color='primary.on'
                bg='primary.solid'
                borderRadius='lg'
                _hover={{ bg: 'primary.hover' }}
                _active={{ bg: 'primary.hover' }}
                onClick={onRetry}
              >
                {retryLabel}
              </Button>
            )}
            {retryCount > 0 && (
              <Text color='text.subtle' fontSize='xs'>
                {retryAttemptLabel}: {retryCount}
              </Text>
            )}
          </>
        ) : (
          <Spinner
            color='primary.solid'
            emptyColor='border.default'
            size='lg'
            speed='0.7s'
            thickness='3px'
          />
        )}
      </Flex>
    </Flex>
  );
};

export default AuthSplash;
